import { generateDarkColor } from '@logto/core-kit';
import { SignInMode } from '../db-entries/index.js';
import { MfaFactor, MfaPolicy, OrganizationRequiredMfaPolicy, SignInIdentifier, } from '../foundations/index.js';
import { adminTenantId, defaultTenantId } from './tenant.js';
import { tdxDarkLogoDataUri, tdxLogoDataUri } from './tdx-branding.js';
/**
 * TDA: the brand blue. ⚠️ Was `#6139F6`, Logto's purple.
 *
 * This is not only the sign-in button. `console/pages/OrganizationDetails` and
 * `ApplicationDetails/components/Branding` both fall back to it, so a fresh
 * install offered Logto's purple as the default colour for every organization
 * and every application anyone created.
 *
 * `#1696e8` and not the logo's `#0090db`: it is the head of the brand gradient
 * (`linear-gradient(135deg, #1696e8, #006bbd)`) and the value both configured
 * tenants already carry, so the seed now matches what a configured install has
 * rather than introducing a third blue.
 */
export const defaultPrimaryColor = '#1696e8';
export const createDefaultSignInExperience = (forTenantId, isCloud) => Object.freeze({
    tenantId: forTenantId,
    id: 'default',
    color: {
        primaryColor: defaultPrimaryColor,
        isDarkModeEnabled: false,
        darkPrimaryColor: generateDarkColor(defaultPrimaryColor),
    },
    branding: {
        // TDA: the TDA mark, inlined. ⚠️ Was `https://logto.io/logo.svg` — the
        // wrong brand, and an outbound request to a third party on every load of
        // the sign-in page. See `./tdx-branding.ts`.
        logoUrl: isCloud ? undefined : tdxLogoDataUri,
        darkLogoUrl: isCloud ? undefined : tdxDarkLogoDataUri,
    },
    hideLogtoBranding: false,
    languageInfo: {
        autoDetect: true,
        fallbackLanguage: 'en',
    },
    termsOfUseUrl: null,
    privacyPolicyUrl: null,
    signUp: {
        identifiers: [isCloud ? SignInIdentifier.Email : SignInIdentifier.Username],
        password: true,
        verify: isCloud,
    },
    signIn: {
        methods: [
            {
                identifier: isCloud ? SignInIdentifier.Email : SignInIdentifier.Username,
                password: true,
                verificationCode: false,
                isPasswordPrimary: true,
            },
        ],
    },
    socialSignInConnectorTargets: [],
    signInMode: SignInMode.SignInAndRegister,
    customCss: null,
    customContent: {},
    customUiAssets: null,
    signUpProfileFields: null,
    passwordPolicy: {},
    mfa: {
        factors: [],
        policy: MfaPolicy.UserControlled,
    },
});
/** @deprecated Use `createDefaultSignInExperience()` instead. */
export const defaultSignInExperience = createDefaultSignInExperience(defaultTenantId, false);
export const createAdminTenantSignInExperience = (options = {}) => Object.freeze({
    ...defaultSignInExperience,
    tenantId: adminTenantId,
    color: {
        ...defaultSignInExperience.color,
        isDarkModeEnabled: true,
    },
    signInMode: SignInMode.Register,
    // TDA: as above. The admin tenant seeds its own row, so changing only the
    // default tenant's left the console — the FIRST screen anyone sees on a new
    // install — still showing Logto's logo.
    branding: {
        logoUrl: tdxLogoDataUri,
        darkLogoUrl: tdxDarkLogoDataUri,
    },
    passwordPolicy: options.disablePwnedPasswordCheck
        ? {
            ...defaultSignInExperience.passwordPolicy,
            rejects: {
                ...defaultSignInExperience.passwordPolicy?.rejects,
                pwned: false,
            },
        }
        : defaultSignInExperience.passwordPolicy,
    mfa: {
        factors: [MfaFactor.TOTP, MfaFactor.WebAuthn, MfaFactor.BackupCode],
        policy: MfaPolicy.NoPrompt,
        organizationRequiredMfaPolicy: OrganizationRequiredMfaPolicy.Mandatory,
    },
});
